import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { getOptimizedImageUrl } from "@/lib/utils";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLocalized } from "@/hooks/useLocalized";

interface PortfolioAlbum {
  id: string;
  title: any;
  description: any;
  cover_image_url: string;
  category: string;
  portfolio_media: Array<{
    id: string;
    media_url: string;
    media_type: string;
    caption: any;
    sort_order: number;
  }>;
}

const Portfolio = () => {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [albums, setAlbums] = useState<PortfolioAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentAlbum, setCurrentAlbum] = useState<PortfolioAlbum | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const { data, error } = await supabase
        .from("portfolio_albums")
        .select(`
          *,
          portfolio_media (
            id,
            media_url,
            media_type,
            caption,
            sort_order
          )
        `)
        .order("sort_order");

      if (error) throw error;

      const albumsWithSortedMedia = (data || []).map(album => ({
        ...album,
        portfolio_media: (album.portfolio_media || []).sort(
          (a: any, b: any) => a.sort_order - b.sort_order
        ),
      }));

      setAlbums(albumsWithSortedMedia);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (album: PortfolioAlbum, imageIndex: number) => {
    setCurrentAlbum(album);
    setCurrentImageIndex(imageIndex);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentAlbum(null);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'unset';
  };

  const goToNext = () => {
    if (!currentAlbum) return;
    setCurrentImageIndex((prev) =>
      prev === currentAlbum.portfolio_media.length - 1 ? 0 : prev + 1
    );
  };

  const goToPrevious = () => {
    if (!currentAlbum) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? currentAlbum.portfolio_media.length - 1 : prev - 1
    );
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;

      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrevious();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentAlbum, currentImageIndex]);

  // Touch swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrevious();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 container mx-auto px-4 py-8 md:py-16">
          <div className="text-center mb-8 md:mb-12">
            <Skeleton className="h-10 md:h-12 w-48 md:w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-full max-w-2xl mx-auto" />
          </div>
          <div className="grid gap-8 md:gap-12">
            {[1, 2, 3].map((i) => (
              <div key={i}>
                <Skeleton className="h-8 w-32 md:w-48 mb-4" />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
                  {[1, 2, 3, 4].map((j) => (
                    <Skeleton key={j} className="aspect-square" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const currentMedia = currentAlbum?.portfolio_media[currentImageIndex];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <main className="flex-1 container mx-auto px-4 py-8 md:py-16">
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{t('portfolio.title', 'Portfolio Gallery')}</h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('portfolio.subtitle', 'Explore our complete collection of photography work')}
          </p>
        </div>

        <div className="space-y-12 md:space-y-16">
          {albums.map((album) => (
            <section key={album.id}>
              <div className="mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">{getLocalized(album.title)}</h2>
                {album.description && (
                  <p className="text-sm md:text-base text-muted-foreground">{getLocalized(album.description)}</p>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
                {album.portfolio_media.map((media, index) => (
                  <Card
                    key={media.id}
                    className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => openLightbox(album, index)}
                  >
                    <CardContent className="p-0">
                      <div className="aspect-square relative">
                        {media.media_type === "image" ? (
                          <img
                            src={getOptimizedImageUrl(media.media_url, { width: 400, height: 400, resize: 'cover' })}
                            alt={getLocalized(media.caption) || getLocalized(album.title)}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <video
                            src={media.media_url}
                            className="w-full h-full object-cover"
                            controls
                          />
                        )}
                      </div>
                      {media.caption && (
                        <div className="p-2 md:p-3">
                          <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">
                            {getLocalized(media.caption)}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      <Footer />

      {/* Full-Screen Lightbox */}
      {lightboxOpen && currentAlbum && currentMedia && (
        <div
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="text-white">
                <h3 className="text-lg md:text-xl font-semibold">{getLocalized(currentAlbum.title)}</h3>
                <p className="text-sm text-white/80">
                  {currentImageIndex + 1} / {currentAlbum.portfolio_media.length}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeLightbox}
                className="text-white hover:bg-white/20"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
          </div>

          {/* Main Image */}
          <div className="flex-1 flex items-center justify-center p-4 md:p-8">
            <img
              src={getOptimizedImageUrl(currentMedia.media_url, { width: 1920, quality: 95 })}
              alt={getLocalized(currentMedia.caption) || getLocalized(currentAlbum.title)}
              className="max-w-full max-h-full object-contain"
              loading="eager"
            />
          </div>

          {/* Navigation Arrows */}
          {currentAlbum.portfolio_media.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPrevious}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 md:h-16 md:w-16"
              >
                <ChevronLeft className="h-8 w-8 md:h-10 md:w-10" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNext}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 md:h-16 md:w-16"
              >
                <ChevronRight className="h-8 w-8 md:h-10 md:w-10" />
              </Button>
            </>
          )}

          {/* Caption */}
          {currentMedia.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6">
              <p className="text-white text-center text-sm md:text-base max-w-3xl mx-auto">
                {getLocalized(currentMedia.caption)}
              </p>
            </div>
          )}

          {/* Thumbnail Strip */}
          <div className="absolute bottom-20 md:bottom-24 left-0 right-0 overflow-x-auto">
            <div className="flex gap-2 px-4 justify-center">
              {currentAlbum.portfolio_media.map((media, index) => (
                <button
                  key={media.id}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded overflow-hidden border-2 transition-all ${index === currentImageIndex
                    ? 'border-white scale-110'
                    : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                >
                  <img
                    src={getOptimizedImageUrl(media.media_url, { width: 100, height: 100, resize: 'cover' })}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
